import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useAuth } from '@/api';
import type { LivePriceData } from '@/api/digital-gold/livePrice';
import { livePricee } from '@/api/digital-gold/livePrice';
import type { VerifyType } from '@/api/digital-gold/sellVerifyPrice';
import { getVerifyPrice } from '@/api/digital-gold/sellVerifyPrice';
import { useLivePriceStore } from '@/api/digital-gold/store/livePriceStore';
// import router from "next/router";
import { useSellVerifyStore } from '@/store/digital-gold/sell-gold-store';
import { useWalletStore } from '@/store/digital-gold/wallet-store';
import type { BaseResponse } from '@/types';

import { LiveGold } from './LiveGold';
import { SellKycModal } from './Modals';
import { UserDgWallet } from './userDgWallet';

type SellForm = {
  activeType: string;
  sellAmountOrGrams: string;
  convertedData: string;
};

// Comment for false commit

// TODO : we will be using onVerify ( which came as a prop ) to navigate to SellGoldCheckout page,
// You can find the commented code below
// { onVerify }: { onVerify: () => void }
// eslint-disable-next-line
export const SellGold = ({
  onVerify,
  amount,
  grams,
  type,
}: {
  onVerify: () => void;
  amount: string;
  grams: string;
  type: string;
}) => {
  const walletData = useWalletStore((state) => state);
  /* eslint-disable-next-line */
  const [livePriceAmount, setLivePriceAmount] = useState(0);
  const [livePriceData, updateLivePrice] = useLivePriceStore((state) => [
    state.pricedata,
    state.updateLivePrice,
  ]);
  const { userDetails } = useAuth();
  const user = {
    id: userDetails?.id,
  };

  // const { livePriceData, refetch } = useLivePrice(); //

  /* eslint-disable-next-line */
  // const [sellAmountOrGrams, setsellAmountOrGrams] = useState<string>('');

  const calculatedGoldValue =
    walletData.goldBalance * livePriceData.saleGoldRate;

  const sellSchema: any = yup.object({
    activeType: yup.string().required(),
    sellAmountOrGrams: yup.string().when('activeType', {
      is: (activeType: string) => activeType === 'rupees',
      then: (x) =>
        x
          .required('*Required')
          .test(
            'sellAmountOrGrams',
            'Minimum ₹50',
            (value) => parseFloat(value) >= 50
          )
          .test(
            'sellAmountOrGrams',
            'Minimum ₹50',
            (value) => parseFloat(value) >= 50
          )
          .test(
            'sellAmountOrGrams',
            `Maximum available balance ${calculatedGoldValue.toFixed(2)}`,
            (value) =>
              // TODO : Replace ceil with precise comparison
              parseFloat(value) <= Math.ceil(calculatedGoldValue)
          ),
    }),
    convertedData: yup.string().when('activeType', {
      is: (activeType: string) => activeType === 'grams',
      then: (x) =>
        x
          .required('*Required')
          .test('convertedData', 'Minimum ₹50', (value) => {
            return parseFloat(value) * livePriceData.saleGoldRate >= 50;
          })
          .test(
            'convertedData',
            `Maximum available grams ${walletData?.goldBalance}`,
            (value) => parseFloat(value) <= walletData?.goldBalance
          ),
      otherwise: (x) => x.notRequired(),
    }),
  });
  const router = useRouter();
  const {
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting: sellSubmitting },
  } = useForm<SellForm>({
    defaultValues: {
      activeType: type,
      sellAmountOrGrams: amount,
      convertedData: grams,
    },
    resolver: yupResolver(sellSchema),
  });
  useEffect(() => {
    if (router.asPath != router.route) {
      setValue('sellAmountOrGrams', amount);
      setValue('convertedData', grams);
      setValue('activeType', type);
    }
  }, [router]);

  // useEffect(() => {
  //   console.log('changes')
  //   setsellAmountOrGrams('');
  //   setValue('sellAmountOrGrams', '');
  //   setValue('convertedData', '');
  // }, [getValues('activeType')]);

  // manual kyc validations
  const openKycRef = useRef<HTMLButtonElement>(null);
  const closeKycRef = useRef<HTMLButtonElement>(null);

  // TODO : we need to uncomment this when we are enabling sell gold
  const storeForSellVerify = useSellVerifyStore();
  // sell in grams 412 error because of prevalidation of wallet
  const onSubmit = async (submitData: SellForm) => {
    // useLivePrice();
    if (userDetails?.isKycVerified) {
      const livePriceAxiosResponse: BaseResponse<LivePriceData> =
        await livePricee().then((data) => data);
      const livePriceResponse = livePriceAxiosResponse?.data?.data;
      if (livePriceResponse !== undefined && 'blockId' in livePriceResponse) {
        updateLivePrice(livePriceResponse);
      }
      const dataItem: VerifyType = {
        id: user.id,
        blockId: livePriceResponse?.blockId || livePriceData.blockId,
        goldInRupees:
          watch('activeType') === 'rupees'
            ? parseFloat(submitData.sellAmountOrGrams)
            : parseFloat(submitData.convertedData) * livePriceData.saleGoldRate,
        weightInGrams:
          watch('activeType') === 'grams'
            ? parseFloat(submitData.convertedData)
            : 0,
      };
      const verifySellPrice = await getVerifyPrice(dataItem);
      if (verifySellPrice.status === 200) {
        onVerify();
        storeForSellVerify.updateResponse(verifySellPrice.data?.data);
      } else {
        openKycRef.current?.click();
      }
    } else {
      openKycRef.current?.click();
    }
  };

  const accountDivision = (amountPercent: string) => {
    const availValue =
      watch('activeType') === 'rupees'
        ? calculatedGoldValue
        : walletData?.goldBalance;
    setValue(
      watch('activeType') === 'rupees' ? 'sellAmountOrGrams' : 'convertedData',
      ((availValue * parseInt(amountPercent, 10)) / 100).toFixed(
        watch('activeType') === 'rupees' ? 2 : 4
      )
    );
  };

  // TODO : Fallback html code based on DG flow enabling status
  const isDGEnabled = true;

  return (
    <>
      <button
        data-toggle="modal"
        data-target="#kycModal"
        ref={openKycRef}
        // style={{ display: 'none' }}
        className="d-none"
      ></button>
      <main className="home loanDetails pledge repayment checkout visitSchedule__body singleFlow--lightBlue sellGold">
        <div className="container">
          <div className="visitSchedule investing">
            <form
              autoComplete="off"
              // TODO : we need to uncomment this when we are enabling sell gold
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="stepper--content active">
                <div className="row">
                  <div className="col-lg-12">
                    {<UserDgWallet note={true} />}

                    <div className="loanDetails__card loanCard">
                      <p className="loanCard__title text--bold text--black sell_p">
                        Sell eSwarna Digital Gold
                      </p>
                      <div className="loanRequirements">
                        {<LiveGold option={'SELL'} callApi={true} />}
                        <div className="radio__container">
                          <label
                            className="radio__circle"
                            onClick={() => {
                              setValue('activeType', 'rupees');
                              setValue('sellAmountOrGrams', '');
                              setValue('convertedData', '');
                            }}
                          >
                            <input
                              type="radio"
                              value="rupees"
                              {...register('activeType')}
                              id="DG_sell-in-rupees"
                            />
                            <span className="radio__circle-checkmark"></span>
                            <p className="text--regular text--black radio__circle-label sell_p">
                              Sell in Rupees
                            </p>
                          </label>
                          <label
                            className="radio__circle"
                            onClick={() => {
                              setValue('activeType', 'grams');
                              setValue('sellAmountOrGrams', '');
                              setValue('convertedData', '');
                            }}
                          >
                            <input
                              type="radio"
                              value="grams"
                              {...register('activeType')}
                              id="DG_sell-in-gms"
                            />
                            <span className="radio__circle-checkmark"></span>
                            <p className="text--regular text--black radio__circle-label sell_p">
                              Sell in Grams
                            </p>
                          </label>
                        </div>
                        {watch('activeType') === 'rupees' ? (
                          <div className="input-container">
                            <div
                              className={`${
                                errors.sellAmountOrGrams
                                  ? 'input_error-border'
                                  : 'input-form'
                              } justify-content-between`}
                            >
                              <input
                                type="number"
                                min="0"
                                step={0.01}
                                // pattern="[0-9]{5}"
                                placeholder="Enter Amount"
                                className="sell_input"
                                {...register('sellAmountOrGrams')}
                                id="DG_sell-in-amount"
                              />
                              <span className="text--semi">
                                {' '}
                                ={' '}
                                {watch('sellAmountOrGrams') === ''
                                  ? 0.0
                                  : (
                                      parseFloat(watch('sellAmountOrGrams')) /
                                      livePriceData.saleGoldRate
                                    ).toFixed(4)}{' '}
                                gms
                              </span>
                            </div>

                            {errors.sellAmountOrGrams && (
                              <p className="text--semi errorMessage withInfo showError">
                                {errors.sellAmountOrGrams?.message}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="input-container">
                            <div
                              className={`${
                                errors.convertedData
                                  ? 'input_error-border'
                                  : 'input-form'
                              } justify-content-between`}
                            >
                              <input
                                type="number"
                                min="0"
                                step={0.0001}
                                placeholder="Enter Grams"
                                className="sell_input"
                                {...register('convertedData')}
                                id="DG_sell-in-grams"
                              />
                              <span className="text--semi">
                                {' '}
                                = ₹{' '}
                                {watch('convertedData') === ''
                                  ? 0.0
                                  : (
                                      parseFloat(watch('convertedData')) *
                                      livePriceData.saleGoldRate
                                    ).toFixed(2)}{' '}
                              </span>
                            </div>
                            {errors.convertedData && (
                              <p className="text--semi errorMessage withInfo showError">
                                {errors.convertedData?.message}
                              </p>
                            )}
                          </div>
                        )}
                        <p className="chips-title text--black text--semi">
                          Sell as percentage holdings
                        </p>
                        <div className="chips--container">
                          <p
                            className="chips text--black text--semi sell_p"
                            onClick={() => accountDivision('25')}
                            id="DG_sell-25"
                          >
                            25%
                          </p>
                          <p
                            className="chips text--black text--semi sell_p"
                            onClick={() => accountDivision('50')}
                            id="DG_sell-50"
                          >
                            50%
                          </p>
                          <p
                            className="chips text--black text--semi sell_p"
                            onClick={() => accountDivision('75')}
                            id="DG_sell-75"
                          >
                            75%
                          </p>
                          <p
                            className="chips text--black text--semi sell_p"
                            onClick={() => accountDivision('100')}
                            id="DG_sell-100"
                          >
                            100%
                          </p>
                        </div>
                        <div className="action-container">
                          {isDGEnabled ? (
                            <div // Payment - change this to button component and uncomment below items
                              // type="submit"
                              // disabled={sellSubmitting}
                              className={`custom__button custom__button--dark text--bold mw-100 w-100
                              ${sellSubmitting ? 'disabled' : ''}
                              `}
                              id="DG_sell-now"
                            >
                              Sell Now
                            </div>
                          ) : (
                            <a
                              target="_blank"
                              href="https://qrco.de/mfone"
                              className="custom__button custom__button--dark text--bold download_sell"
                              // style={{
                              //   margin: 'auto',
                              //   maxWidth: 'fit-content',
                              // }}
                              id="DG_sell-download"
                            >
                              Download App to Sell Gold
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="text--gray text--regular pledge__info">
                      <span className="text--darkBlue text--bold">Note:</span>{' '}
                      You can sell eSwarna Digital Gold worth minimum ₹50
                    </p>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </main>
      <SellKycModal openKycRef={openKycRef} closeKycRef={closeKycRef} />
    </>
  );
};
